import * as React from 'react';

import {formEncodedApi} from 'nutshell-core/api';

import {BulletedList} from 'shells/bulleted-list/bulleted-list';
import {SyncRegularIcon} from 'shells/icon/svg/sync-regular-icon';
import {IntegrationWidget} from 'shells/integration-widget/integration-widget';
import {SpacerGroup} from 'shells/spacer/spacer-group';
import {MarketingBody} from 'shells/typography/marketing-body';
import {MarketingTitle} from 'shells/typography/marketing-title';

import styles from './constant-contact-connected.module.css';

type Props = {
    email: string | null | undefined;
    isContactSyncActive: boolean;
    refetchIntegration: () => Promise<any>;
    onConstantContactConnect: () => Promise<any>;
};

const BULLETS = [
    'See up-to-date open, click, and bounce rates from Constant Contact on your Nutshell people pages',
    'Send leads and people to lists in Constant Contact in bulk or individually with one click',
    'Filter people by Constant Contact list membership and engagement rating',
];

type State = {
    syncStatus: 'enabled' | 'enabling' | 'disabled' | 'disabling';
};
type Action = {
    type: 'CHANGE_SYNC' | 'RESOLVE_CHANGE_SYNC' | 'FAIL_CHANGE_SYNC';
};

/* This is run once when the component mounts, to set initial state from props */
// @ts-expect-error
function init({isContactSyncActive}) {
    return {
        syncStatus: isContactSyncActive ? 'enabled' : 'disabled',
    };
}

/* So far, just tracking sync status in this little reducer, connection state can be handled by normal useState */
function reducer(state: State, action: Action) {
    switch (action.type) {
        case 'CHANGE_SYNC':
            return {
                ...state,
                syncStatus: state.syncStatus === 'disabled' ? 'enabling' : 'disabling',
            };
        case 'RESOLVE_CHANGE_SYNC':
            return {
                ...state,
                syncStatus: state.syncStatus === 'enabling' ? 'enabled' : 'disabled',
            };
        case 'FAIL_CHANGE_SYNC':
            return {
                ...state,
                syncStatus: state.syncStatus === 'enabling' ? 'disabled' : 'enabled',
            };
        default:
            return state;
    }
}

export function ConstantContactConnected(props: Props) {
    const {isContactSyncActive} = props;

    const [state, dispatch] = React.useReducer<
        // @ts-expect-error
        State,
        Action,
        {
            isContactSyncActive: boolean;
        }
    >(reducer, {isContactSyncActive}, init);

    const handleDisconnectConstantContact = () => {
        formEncodedApi
            .post('/setup/instant-integration/disable-integration.json', {
                integrationType: 'ConstantContact',
            })
            .then(() => {
                return props.refetchIntegration();
            });
    };

    // This needs to be wrapped to remove the promise return
    const handleConnectConstantContact = () => {
        props.onConstantContactConnect();
    };

    const toggleSync = () => {
        // @ts-expect-error
        dispatch({type: 'CHANGE_SYNC'});
        formEncodedApi
            .post('/setup/instant-integration/edit-constant-contact-sync.json', {
                // If contact sync is currently enabled, disable (0), otherwise, enable (1)
                enableSync: props.isContactSyncActive ? 0 : 1,
            })
            .then(() => {
                return props.refetchIntegration();
            })
            .then(() => {
                // @ts-expect-error
                dispatch({type: 'RESOLVE_CHANGE_SYNC'});
            })
            .catch(() => {
                // @ts-expect-error
                dispatch({type: 'FAIL_CHANGE_SYNC'});
            });
    };

    return (
        <div>
            <SpacerGroup mb={32}>
                <MarketingTitle>Connect to Constant Contact</MarketingTitle>
                <BulletedList size='big' bullets={BULLETS} />
                <IntegrationWidget
                    account={props.email}
                    status='enabled'
                    logo={
                        <img
                            src='/include/images/integration-logos/constant-contact.png'
                            className={styles.logo}
                        />
                    }
                    onConnectClick={handleConnectConstantContact}
                    onDisconnectClick={handleDisconnectConstantContact}
                />
            </SpacerGroup>
            <MarketingTitle>
                Automatically import Constant Contact people into Nutshell
            </MarketingTitle>
            <MarketingBody>
                Turn on this importer to create people in Nutshell for everybody in Constant
                Contact. This is helpful when you have a signup form on your website which adds new
                prospects to a list. We’ll automatically create the appropriate custom fields.
            </MarketingBody>
            <IntegrationWidget
                account={`Contact importer is turned ${props.isContactSyncActive ? 'on' : 'off'}`}
                // @ts-expect-error
                status={state.syncStatus}
                statusMessage={props.isContactSyncActive ? 'The importer runs every 24 hours' : ''}
                logo={<SyncRegularIcon size={32} wrapWithDiv={false} />}
                onConnectClick={toggleSync}
                onDisconnectClick={toggleSync}
                actionStyle='switch'
            />
        </div>
    );
}
